import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { LoaderButton } from 'framework/components/buttons/LoaderButton';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { SortableTable } from 'framework/table/SortableTable';
import { IProductCatalogItem, ISalesLineForAssuranceDto, salesQuery_queryNonAssured } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import { NoRecordsFound } from '../../../../framework/components/NoRecordsFound';
import { ISearchAndSelectLineWithAssurance } from '../ISearchAndSelectLineWithAssurance';
import { canSelect } from './canSelect';
import { createHeaders } from './createHeaders';
import { ISalesLineForAssuranceDtoWithSelection } from './ISalesLineForAssuranceDtoWithSelection';

interface IProps<TLine extends ISearchAndSelectLineWithAssurance> extends DialogProps {
	confirm: (lines: ISalesLineForAssuranceDto[]) => void;
	cancel: VoidFunction;
	assurance: IProductCatalogItem;
	customerAccountId: string;
	lines: TLine[];
}

export const SelectLinesFromPreviousSaleToAssureDialog = <TLine extends ISearchAndSelectLineWithAssurance>({
	confirm,
	cancel,
	assurance,
	customerAccountId,
	lines: salesLines,
	...rest
}: IProps<TLine>) => {
	const strings = useLocalization();
	const [qLines] = useApiEffect(salesQuery_queryNonAssured, customerAccountId, assurance.id!);
	const fQLines = useMemo(() => qLines?.filter(t => canSelect(assurance, t, salesLines)), [qLines, assurance, salesLines]);
	const [lines, setLines] = useState<ISalesLineForAssuranceDtoWithSelection[]>([]);
	const headers = useMemo(() => createHeaders(lines, setLines, strings), [lines, setLines, strings]);

	useEffect(() => {
		if (fQLines) {
			setLines(fQLines.map(t => ({ ...t, isSelected: false })));
		}
	}, [fQLines]);

	if (fQLines === undefined) {
		return <div></div>;
	}

	return (
		<Dialog
			{...rest}
			PaperProps={{ style: { minWidth: 400 } }}
			maxWidth='xl'>
			<DialogTitle>{strings.selectWhat(strings.lines)}</DialogTitle>
			<DialogContent
				dividers
				style={{ padding: 0 }}>
				{lines.length > 0 && (
					<SortableTable<ISalesLineForAssuranceDtoWithSelection>
						padding={8}
						headers={headers}
						getKey={t => t.id}
						values={lines}
						defaultSortParams={{ property: 'salesDate', direction: 'desc' }}
					/>
				)}
				{lines.length === 0 && <NoRecordsFound withPadding>{strings.noPreviousSoldLines}</NoRecordsFound>}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={cancel}
					color='primary'>
					{strings.cancel}
				</Button>
				<LoaderButton
					isLoading={false}
					onClick={() => confirm(lines.filter(t => t.isSelected))}
					disabled={lines.filter(t => t.isSelected).length === 0}
					color='primary'>{`${strings.select} (${lines.filter(t => t.isSelected).length})`}</LoaderButton>
			</DialogActions>
		</Dialog>
	);
};
